import { fabric } from 'fabric';
import { IObjectOptions } from 'fabric/fabric-impl';
import { uniqueId } from 'lodash';
import { Subject } from 'rxjs';
import Vec2 from 'vec2';

import { ClickableCircleActions } from './drawingTool.types';
import {
  ClipShapeListType,
  addDiagonalCornerIfNeeded,
  addDiagonalCornerToShapeWithInternalDiagonalCorner,
  addRoundedCornerRadiusArc,
  buildBumpOutArrayOfPoints,
  createBumpOutPath,
  createBumpOutWithHalfEdgeLength,
  determineShapeLabelPosition,
  getBoneOutline,
  getShapeTextPos,
  processShapeElements,
  setEdgeStartAndEndDisplayPoints,
  setRadiiCenterPoints,
  setShapeNameAndColors,
  updateArrowsInfo,
  updateEdgesWithNewOutlineData,
  updateRemovedElementsHistory,
} from './utils';
import { calculateNormalVector } from './utils/rebuildShapeFromNewSkeleton/calculateNormalVector';

import {
  BLACK,
  BUMPOUT_LENGTH,
  CANVAS_OBJECT_TYPE_PREFIX,
  CANVAS_OBJECT_TYPE_SUFFIX,
  CORNER_RADIUS_SIGN,
  DEFAULT_BUMPOUT_DEPTH,
  DEFAULT_BUMPOUT_LENGTH,
  DEFAULT_CORNER_RADIUS,
  DEFAULT_LARGEST_SHAPE_DIMENSION,
  EDGE_LENGTH_INPUT_LIMIT,
  EDGE_STROKE_WIDTH,
  ELLIPSIS,
  GREY_STROKE_WIDTH,
  GREY_STROKE_WIDTH_BACKSPLASH,
  KEYS,
  LABEL_ELLIPSIS_LIMIT,
  LABEL_LEFT_COLLISION_MARGIN,
  LABEL_OFFSET,
  LABEL_STROKE_WIDTH,
  LOCAL_STORAGE_KEY,
  MEASUREMENT_STROKE_WIDTH,
  MINIMUM_CUSTOM_SIDE_LENGTH,
  OBJECT_SORT_DELAY_LOOPS,
  OFFSET_BACKSPLASH,
  OFFSET_MEASUREMENT,
  OFFSET_MEASUREMENT_TEXT,
  ROUGH_ESTIMATE_FOR_SHAPE_LABEL_LENGTH,
  SELECTION_CIRCLES,
  SELECTION_CIRCLE_OPACITY,
  SELECTION_CIRCLE_RADIUS,
  SHAPE_LABEL_MARGIN,
  SHAPE_NAME_MAX_LENGTH,
  SLAB_SCALE,
} from '~/core/constants';
import {
  EditableCornerLabel,
  EditableCornerLabelHandler,
  EditableLabel,
  EditableLengthLabel,
} from '~/core/engine/EditableLabel';
import {
  CircleAction,
  DiagonalCornerOptions,
  LineDirection,
  ShapeType,
  isBumpOut,
  isRadiusOut,
} from '~/core/enums';
import {
  calculateRadiusFromArc,
  cleanSkeletonPoints,
  convertFromRadiansToDegree,
  findArrowByEdgeId,
  getBumpOutArrowsFromOutlines,
  getDirection,
  getDistanceBetween,
  getMidpoint,
  getOutlineCustomSideEdges,
  getOutlineDiagonalFlagsFromModifiedShape,
  getOutlinePointsFromModifiedShape,
  getOutlinePointsFromShape,
  getOutlinePts,
  getRectangleSideItemArrowsFromOutlines,
  getRefinedEdgePoints,
  isPointInsidePolygon,
  polygonCollisions,
  rotatePointAboutCenterPoint,
  roundToArrowLengthGranularity,
  subtractVectors,
} from '~/core/helpers';
import { getLocalData, setLocalData } from '~/core/helpers/storageHelper';
import {
  ArrowInfo,
  ColorModel,
  CornerInfo,
  CornerMap,
  CornerRadiusModel,
  DiagonalCornerModel,
  DrawingAppliance,
  DrawingEdge,
  DrawingShape,
  LineSegment,
  PredefinedShape,
  RadiusRangeValues,
  SideItem,
  Skeleton,
  getCornerRadiusFromSize,
  getMinMaxFromCorner,
  isCornerRadiusModel,
} from '~/core/models';
import { CustomSideModel } from '~/core/models/predefined-custom-side.model';
import {
  DEFAULT_BUMPOUT,
  applianceImages,
  applyFillToBacksplash,
  applyFillToMainShape,
  calcMaxAllowedRadius,
  calculateEdgeConstraints,
  calculatePatternTransformation,
  cloneDrawingShape,
  createArrowsGroup,
  createCornerInfo,
  createDrawingShape,
  createSideItem,
  darkenAllLabels,
  enforceRadiusConstraints,
  formatCornerRadiusLabel,
  getArrowsFromOutlines,
  getLabelsForArrows,
  getLetterStartFromDrawingEdges,
  hasEdgeName,
  hasRequiredProps,
  hasShapeMeasureLabel,
  hideAllLabels,
  isAppliance,
  isBacksplashGroup,
  isMainShape,
  isShapeGroup,
  isShapeLabel,
  isSomething,
  lightenAllLabels,
  maxAvailableForCornerOptions,
  removeLabelLetters,
} from '~/core/utils';
import { findImageByName } from '~/core/utils/drawingTool/drawingTool.utils';
import {
  ensureMutable,
  forceAssignProperty,
} from '~/core/utils/objectMutability.utils';
import theme from '~/theme/theme';

const {
  drawingTool: {
    drawingShape: { stroke },
    borderColor,
    secondaryBorderColor,
    fontFamily,
  },
} = theme.light;

export const drawingTool = {
  buildShapeFromSkeleton: (
    sData: Skeleton,
    hasExtraEdging: boolean
  ): DrawingShape | undefined => {
    const clonedSkeleton: Skeleton = sData.map((bone) => ({
      pos: bone.pos,
      depth: bone.depth,
    }));

    const cleanSkeleton = cleanSkeletonPoints(clonedSkeleton);

    if (!cleanSkeleton) {
      return undefined;
    }

    const outlinePoints = getOutlinePts(cleanSkeleton);

    if (!outlinePoints) {
      return undefined;
    }

    const corners: CornerMap = {};

    outlinePoints
      .filter((point) =>
        hasRequiredProps(point, ['coords', 'skIndex', 'isUpper'])
      )
      .forEach((point) => {
        const id = uniqueId();
        const corner = createCornerInfo(
          id,
          point.coords,
          point.skIndex,
          point.isUpper
        );

        corner.isVisibleInCanvas = true;
        corner.hasPredefinedExtraEdging = hasExtraEdging;

        corners[id] = corner;
      });

    const dShapeId = uniqueId();
    const cornersList = Object.values(corners);
    const edges = Object.keys(corners).map((key, index) => {
      const startCornerId = corners[key].id;
      const nextIndex = index === cornersList.length - 1 ? 0 : index + 1;
      const { id: endCornerId } = cornersList[nextIndex];
      const dirVec = new Vec2(
        corners[endCornerId].coords.x - corners[startCornerId].coords.x,
        corners[endCornerId].coords.y - corners[startCornerId].coords.y
      );

      const normalVec = calculateNormalVector(dirVec);
      const isVisibleInCanvas = true;
      const sideItem: SideItem = createSideItem(0, [], undefined);
      const bumpOut = DEFAULT_BUMPOUT;
      const startDisplayVec = new Vec2(0, 0);
      const endDisplayVec = new Vec2(0, 0);
      const drawingEdge: DrawingEdge = {
        id: uniqueId(),
        startCornerId,
        endCornerId,
        startDisplayVec,
        endDisplayVec,
        normalVec,
        dirVec,
        isVisibleInCanvas,
        drawingShapeId: dShapeId,
        sideItem,
        bumpOut,
      };

      Object.defineProperty(drawingEdge, 'startDisplayVec', {
        value: drawingEdge.startDisplayVec,
        writable: true,
        enumerable: true,
        configurable: true,
      });

      Object.defineProperty(drawingEdge, 'endDisplayVec', {
        value: drawingEdge.endDisplayVec,
        writable: true,
        enumerable: true,
        configurable: true,
      });

      Object.defineProperty(drawingEdge, 'dirVec', {
        value: drawingEdge.dirVec,
        writable: true,
        enumerable: true,
        configurable: true,
      });

      Object.defineProperty(drawingEdge, 'normalVec', {
        value: drawingEdge.normalVec,
        writable: true,
        enumerable: true,
        configurable: true,
      });

      return drawingEdge;
    });

    // constructor for the shape should really be this whole function, but just using the class for now
    const drawingShape = createDrawingShape({
      skeleton: cleanSkeleton,
      type: ShapeType.PREDEFINED_SHAPE,
    });

    drawingShape.canvasObjectName =
      CANVAS_OBJECT_TYPE_PREFIX.SHAPE_MAIN + '_' + uniqueId();
    drawingShape.cornerMap = corners;
    drawingShape.drawingEdges = edges;
    drawingShape.removedElementsHistory = { edgesAndCorners: [] };
    drawingShape.id = dShapeId;

    const outlinePointsCoords = outlinePoints
      .map((item) => item.coords)
      .filter(isSomething);

    drawingShape.highlightPoints = outlinePointsCoords;

    // add initial arrows
    drawingShape.arrowsInfo = getArrowsFromOutlines(
      outlinePointsCoords,
      outlinePointsCoords,
      { diagonals: [], cornerIds: [] },
      []
    ).map((arrow, index) => ({
      ...arrow,
      drawingEdgeId: drawingShape?.drawingEdges[index]?.id,
      isVisibleInCanvas: true,
      isActiveForEdgeCalc: true,
    }));

    // find the edge for the letter A
    if (!drawingShape.establishedLetterStartIndex) {
      // prevents rotating shapes from swapping out edge letters
      drawingShape.establishedLetterStartIndex =
        getLetterStartFromDrawingEdges(drawingShape);

      const labels = getLabelsForArrows(
        drawingShape.arrowsInfo,
        drawingShape.establishedLetterStartIndex
      );

      // map the new labels into the arrows
      drawingShape.arrowsInfo?.forEach(
        (arrowInfo: ArrowInfo, index: number) => {
          arrowInfo.label = labels[index];
        }
      );
    }

    calculateEdgeConstraints(drawingShape);

    return drawingShape;
  },

  rebuildShapeFromNewSkeleton: (
    sData: Skeleton,
    oldShape: DrawingShape
  ): void => {
    // may have some similar (ok duplicate) code from buildShapeFromSkeleton:
    // takes an existing shape and applies a new skeleton to it, adjusting all that needs
    // to be adjusted and leaving alone that which does not

    // get new outline data from new skeleton
    const outlineData = getOutlinePts(sData);

    if (!outlineData) {
      return;
    }

    const { edges, corners } = updateEdgesWithNewOutlineData(
      oldShape,
      outlineData
    );

    updateRemovedElementsHistory(oldShape.removedElementsHistory);

    oldShape.skeleton = sData;
    oldShape.cornerMap = corners;
    oldShape.drawingEdges = edges;

    const outlinePoints = outlineData
      .map((item) => item.coords)
      .filter((item) => item !== undefined);

    oldShape.highlightPoints = outlinePoints;

    updateArrowsInfo(oldShape, outlinePoints);
  },

  buildFauxShape: (): DrawingShape => {
    const fauxShape = createDrawingShape({
      skeleton: [],
      type: ShapeType.FAUX_SHAPE,
    });

    fauxShape.arrowsInfo = [];
    fauxShape.id = ShapeType.FAUX_SHAPE;

    return fauxShape;
  },

  cloneShapes: (shapes: DrawingShape[]): DrawingShape[] =>
    shapes.map((shape: DrawingShape) => cloneDrawingShape(shape, false, false)),

  /**
   * Builds a shape using the `predefinedShape` data
   * @param predefinedShape
   * @returns the actual built shape
   */
  buildShapeFromPredefinedShape: (
    predefinedShape: PredefinedShape
  ): DrawingShape[] =>
    predefinedShape.skeletonData
      .map((skeleton) =>
        drawingTool.buildShapeFromSkeleton(
          skeleton,
          predefinedShape?.hasInteriorDiagonal
        )
      )
      .filter((shape) => shape !== undefined),

  addDrawingShape: ({
    canvas,
    shapes,
    onEdit,
    useOverlay,
    activeDrawingShape,
    activeAppliance,
    cornerRadii,
  }: {
    canvas: fabric.Canvas;
    shapes: DrawingShape[];
    onEdit: (editedArrow: ArrowInfo, drawingShape: DrawingShape) => void;
    useOverlay: boolean;
    activeDrawingShape?: DrawingShape;
    activeAppliance?: string;
    cornerRadii: CornerRadiusModel[];
  }): void => {
    const opt: IObjectOptions = {
      fill: 'transparent',
      selectable: false,
      stroke: borderColor,
      strokeWidth: GREY_STROKE_WIDTH,
      strokeUniform: true,
      //Movement:
      lockMovementX: false,
      lockMovementY: false,
      //Scaling:
      lockScalingX: true,
      lockScalingY: true,
      //Rotation:
      lockRotation: true,
      //frame/bounidngBox:
      hasControls: false,
      hasBorders: false,
    };

    shapes
      .filter((shape: DrawingShape) => shape.type !== ShapeType.FAUX_SHAPE)
      .forEach((shape) => {
        if (!shape?.cornerMap) {
          return;
        }

        if (
          getLocalData(LOCAL_STORAGE_KEY.LOAD_ESTIMATE) &&
          !getLocalData(LOCAL_STORAGE_KEY.FIRST_SHAPE_LOAD)
        ) {
          setLocalData(LOCAL_STORAGE_KEY.FIRST_SHAPE_LOAD, true);
        }

        hideAllLabels(shape.editableLabels);
        shape.editableLabels = [];

        //The placement of this function is important. Placing else where may cause errors
        triggerEdgeAndCornersMutations(shape, cornerRadii);

        opt.name = shape?.canvasObjectName;
        const outlinePoints = getOutlinePointsFromModifiedShape(shape);
        const outline = new fabric.Polygon(outlinePoints, opt);

        shape.fabricGroup.fill = 'transparent';
        shape.fabricGroup.stroke = 'transparent';
        shape.fabricGroup.selectable = true;
        shape.fabricGroup.lockMovementX = false;
        shape.fabricGroup.lockMovementY = false;
        shape.fabricGroup.lockScalingX = true;
        shape.fabricGroup.lockScalingY = true;
        shape.fabricGroup.lockRotation = true;
        shape.fabricGroup.hasControls = false;
        shape.fabricGroup.hasBorders = false;
        shape.fabricGroup.perPixelTargetFind = true;

        shape.fabricGroup.remove(...shape.fabricGroup.getObjects());
        shape.fabricGroup.addWithUpdate(outline);
        shape.fabricGroup.name =
          shape.canvasObjectName + CANVAS_OBJECT_TYPE_SUFFIX.SHAPE_GROUP;

        drawingTool.addDrawingEdgeToCanvas(
          canvas,
          shape,
          onEdit,
          useOverlay,
          activeDrawingShape
        );
        drawingTool.addAppliancesToCanvas(canvas, shape, activeAppliance);

        canvas.add(shape.fabricGroup);
      });

    shapes
      .filter((shape: DrawingShape) => shape.type === ShapeType.FAUX_SHAPE)
      .forEach((shape) =>
        drawingTool.addAppliancesToCanvas(canvas, shape, activeAppliance)
      );

    drawingTool.tryObjectSorting(canvas, shapes);
  },

  tryObjectSorting: (canvas: fabric.Canvas, shapes: DrawingShape[]) => {
    let countDown = OBJECT_SORT_DELAY_LOOPS;

    const sortLoop = () => {
      if (
        countDown === 0 ||
        drawingTool.allShapesAndAppliancesAreDrawn(canvas, shapes)
      ) {
        drawingTool.bringAllLabelsForward(canvas);

        return;
      }

      countDown -= 1;
      requestAnimationFrame(sortLoop);
    };

    requestAnimationFrame(sortLoop);
  },

  /**
   * Validate if all shapes and appliances are correctly drawn in the canvas
   * @param canvas
   * @param drawingShapes
   * @returns `true` if both `expected` and `drawn` shapes count are equal, `false` otherwise
   */
  allShapesAndAppliancesAreDrawn: (
    canvas: fabric.Canvas,
    drawingShapes: DrawingShape[]
  ): boolean => {
    const countExpectedItems = (shapes: DrawingShape[]) =>
      shapes.reduce((total, shape) => {
        const shapeCount = shape.id !== ShapeType.FAUX_SHAPE ? 1 : 0;
        const applianceCount = shape.appliances.length;

        return total + shapeCount + applianceCount;
      }, 0);

    const countDrawnItems = (canvas: fabric.Canvas) => {
      const drawnItemSet = new Set<string>();

      canvas.getObjects().forEach((obj) => {
        if (isMainShape(obj) && obj.name) {
          drawnItemSet.add(obj.name);
        }

        if (isAppliance(obj) && obj.name) {
          drawnItemSet.add(obj.name);
        }

        if (isShapeGroup(obj)) {
          (obj as fabric.Group)
            .getObjects()
            .filter(isAppliance)
            .forEach((item) => {
              if (item.name) {
                drawnItemSet.add(item.name);
              }
            });
        }
      });

      return drawnItemSet.size;
    };

    const expectedCount = countExpectedItems(drawingShapes);
    const drawnCount = countDrawnItems(canvas);

    return expectedCount === drawnCount;
  },

  /**
   * Do the necessary calculations on edges in order to apply a corner
   * @param shape the drawn shape
   */
  edgeAndCornerCalcs: (shape: DrawingShape): void => {
    // first loop through sets the edge start and end display points
    shape.drawingEdges.forEach((edge, edgeIndex) => {
      setEdgeStartAndEndDisplayPoints(shape, edge, edgeIndex);
    });

    // second loop through sets the radii center points (based on the start and end points of the edges from the first loop)
    shape.drawingEdges.forEach((edge, edgeIndex) => {
      setRadiiCenterPoints(shape, edge, edgeIndex);
    });

    Object.values(shape.cornerMap).forEach((corner) =>
      calcMaxAllowedRadius(shape, corner)
    );
  },

  constraintRadiiSize: (
    shape: DrawingShape,
    cornerRadii: CornerRadiusModel[]
  ) =>
    Object.values(shape?.cornerMap).filter((corner) => {
      if (!corner.size) {
        drawingTool.setCornerRadiusSize(
          corner,
          DEFAULT_CORNER_RADIUS,
          cornerRadii
        );

        return true;
      }

      if (!corner.isVisibleInCanvas || corner.diagonalLength) {
        return false;
      }

      const constrainedSize = enforceRadiusConstraints(corner, corner.size);

      if (constrainedSize === corner.size) {
        return false;
      }

      drawingTool.setCornerRadiusSize(corner, constrainedSize, cornerRadii);

      return true;
    }),

  isApplianceOnCountertop: (
    newCenterPoint: Vec2,
    appliance: DrawingAppliance,
    shape: DrawingShape,
    strict: boolean
  ): boolean => {
    if (strict) {
      const applianceCorners = [
        new Vec2(
          newCenterPoint.x - appliance.width / 2,
          newCenterPoint.y - appliance.height / 2
        ),
        new Vec2(
          newCenterPoint.x + appliance.width / 2,
          newCenterPoint.y - appliance.height / 2
        ),
        new Vec2(
          newCenterPoint.x + appliance.width / 2,
          newCenterPoint.y + appliance.height / 2
        ),
        new Vec2(
          newCenterPoint.x - appliance.width / 2,
          newCenterPoint.y + appliance.height / 2
        ),
      ];

      const rotatedApplianceCorners = applianceCorners.map((corner) =>
        rotatePointAboutCenterPoint(corner, newCenterPoint, 0 - appliance.angle)
      );

      // check to ensure NO appliance corners IS OUTSIDE the countertop polygon
      let allCornersIn = !rotatedApplianceCorners.some(
        (rotCorner) =>
          !isPointInsidePolygon(
            getOutlinePointsFromModifiedShape(shape),
            rotCorner
          )
      );

      // also check to ensure NO countertop corner IS INSIDE the appliance outline
      allCornersIn =
        allCornersIn &&
        !getOutlinePointsFromModifiedShape(shape).some((counterCorner) =>
          isPointInsidePolygon(rotatedApplianceCorners, counterCorner)
        );

      return allCornersIn;
    }

    return isPointInsidePolygon(
      getOutlinePointsFromModifiedShape(shape),
      newCenterPoint
    );
  },

  addAppliancesToCanvas: (
    canvas: fabric.Canvas,
    shape: DrawingShape,
    activeAppliance?: string
  ): void => {
    shape.appliances.forEach(async (appliance) => {
      const applianceType = appliance.applianceModel.applianceType;
      const getImageSource = applianceImages[applianceType];

      if (!getImageSource) {
        return;
      }

      const applianceName = `${CANVAS_OBJECT_TYPE_PREFIX.APPLIANCE}_${appliance.id}`;

      // Check if appliance image already exists on canvas
      const existingApplianceImage = findImageByName(
        canvas.getObjects(),
        applianceName
      );

      // Also check in shape groups
      let existingInGroup: fabric.Image | undefined;

      if (!existingApplianceImage && shape.fabricGroup) {
        existingInGroup = findImageByName(
          shape.fabricGroup.getObjects(),
          applianceName
        );
      }

      const existingImage = existingApplianceImage || existingInGroup;

      if (existingImage) {
        // Reuse existing image - just update its properties
        let scale = 1 / 10; // default scale for appliances with no sizing

        if (appliance.height && existingImage.height) {
          scale = appliance.height / existingImage.height;
        }

        existingImage.set({
          left: appliance.centerPoint.x,
          top: appliance.centerPoint.y,
          angle: appliance.angle,
          centeredRotation: true,
          originX: 'middle',
          originY: 'middle',
          selectable: true,
          lockRotation: true,
          lockScalingX: true,
          lockScalingY: true,
          hasControls: false,
          scaleX: scale,
          scaleY: scale,
        });

        // Move appliance between canvas and group if needed
        const shouldBeOnCanvas =
          activeAppliance === applianceName ||
          shape.type === ShapeType.FAUX_SHAPE;
        const isOnCanvas = canvas.getObjects().includes(existingImage);

        if (shouldBeOnCanvas && !isOnCanvas) {
          // Remove from group and add to canvas
          if (shape.fabricGroup) {
            shape.fabricGroup.remove(existingImage);
          }
          canvas.add(existingImage);
          if (activeAppliance === applianceName) {
            canvas.setActiveObject(existingImage);
          }
        } else if (!shouldBeOnCanvas && isOnCanvas) {
          // Remove from canvas and add to group
          canvas.remove(existingImage);
          if (shape.fabricGroup) {
            shape.fabricGroup.addWithUpdate(existingImage);
          }
        } else if (shouldBeOnCanvas && activeAppliance === applianceName) {
          canvas.setActiveObject(existingImage);
        }

        return;
      }

      // Create new image only if it doesn't exist
      const applianceImageSource = await getImageSource(appliance);

      if (!applianceImageSource) {
        return;
      }

      fabric.Image.fromURL(
        applianceImageSource,
        (applianceImage: fabric.Image) => {
          let scale = 1 / 10; // default scale for appliances with no sizing

          // otherwise, use the ratio of the appliance's height to its image height for the scale
          if (appliance.height && applianceImage.height) {
            scale = appliance.height / applianceImage.height;
          }
          applianceImage.set({
            left: appliance.centerPoint.x,
            top: appliance.centerPoint.y,
            angle: appliance.angle,
            centeredRotation: true,
            originX: 'middle',
            originY: 'middle',
            selectable: true,
            lockRotation: true,
            lockScalingX: true,
            lockScalingY: true,
            hasControls: false,
            name: applianceName,
            scaleX: scale,
            scaleY: scale,
          });

          if (
            activeAppliance === applianceName ||
            shape.type === ShapeType.FAUX_SHAPE
          ) {
            canvas.add(applianceImage);
            if (activeAppliance === applianceName) {
              canvas.setActiveObject(applianceImage);
            }
          } else {
            shape.fabricGroup.addWithUpdate(applianceImage);
          }
        }
      );
    });
  },

  addDrawingEdgeToCanvas: (
    canvas: fabric.Canvas,
    shape: DrawingShape,
    onEdit: (editedArrow: ArrowInfo, drawingShape: DrawingShape) => void,
    useOverlay: boolean,
    activeDrawingShape?: DrawingShape
  ): void => {
    const optEdge: IObjectOptions = {
      fill: 'transparent',
      selectable: false,
      stroke: stroke.normal,
      strokeLineCap: 'round',
      strokeWidth: EDGE_STROKE_WIDTH,
      data: {
        parentName: shape.canvasObjectName,
        highlightable: true,
      },
    };

    shape.drawingEdges
      .filter((edge) => edge.isVisibleInCanvas)
      .forEach((edge) => {
        const endPt = shape.cornerMap[edge.endCornerId];

        if (
          endPt.hasPredefinedExtraEdging &&
          !endPt.diagonalLength &&
          endPt.angle &&
          endPt.angle < 0
        ) {
          addDiagonalCornerToShapeWithInternalDiagonalCorner(endPt);
        }

        if (
          endPt.diagonalLength &&
          endPt.replacementStartCoords &&
          endPt.replacementEndCoords &&
          (endPt.replacementStartCoords.x !== endPt.replacementEndCoords.x ||
            endPt.replacementStartCoords.y !== endPt.replacementEndCoords.y)
        ) {
          addDiagonalCornerIfNeeded(shape, edge, endPt, optEdge);
        } else if (
          endPt.angle &&
          endPt.size &&
          endPt.size > 0.75 &&
          endPt.isVisibleInCanvas &&
          endPt.radiusCenterPoint
        ) {
          addRoundedCornerRadiusArc(shape, edge, endPt);
        }

        // add the edges, shortened for corner radii or diagonals if needed
        if (edge.customSideType === undefined) {
          // add straight edge
          optEdge.name = `${CANVAS_OBJECT_TYPE_PREFIX.SHAPE_EDGE}_${edge.id}`;
          const drawingPoints = getRefinedEdgePoints(
            [
              edge.startDisplayVec.x,
              edge.startDisplayVec.y,
              edge.endDisplayVec.x,
              edge.endDisplayVec.y,
            ],
            optEdge.strokeWidth
          );

          const edgeObj = new fabric.Line(drawingPoints, optEdge);

          shape.fabricGroup.addWithUpdate(edgeObj);
        } else if (isRadiusOut(edge)) {
          shape.fabricGroup.addWithUpdate(
            drawingTool.addRadiusOut(edge, false, useOverlay)
          );
        } else if (isBumpOut(edge)) {
          shape.fabricGroup.addWithUpdate(
            drawingTool.addBumpOut(edge, false, useOverlay)
          );
          drawingTool.addBumpOutArrows(
            canvas,
            shape,
            edge,
            onEdit,
            activeDrawingShape
          );
        }
      });

    calculateEdgeConstraints(shape);
  },

  addRadiusOut: (
    edge: DrawingEdge,
    forClipping: boolean,
    useOverlay: boolean
  ): fabric.Path => {
    if (!edge.radiusSagitta) {
      throw new Error(`Missing radiusSagitta in edge ${edge}`);
    }

    // use the potentially modified edge endpoints to find the radius center point for the radius out
    const chordLength = getDistanceBetween(
      edge.startDisplayVec,
      edge.endDisplayVec
    );
    const radius = calculateRadiusFromArc(chordLength, edge.radiusSagitta);
    const halfChord = chordLength / 2;

    if (!forClipping) {
      const newRealRadius =
        (edge.radiusSagitta * edge.radiusSagitta + halfChord * halfChord) /
        (2 * edge.radiusSagitta);

      const mutableEdge = ensureMutable(edge);

      forceAssignProperty(mutableEdge, 'realRadius', newRealRadius);
    }

    const bigArc = edge.radiusSagitta > halfChord ? '1' : '0';

    const pathString =
      'M ' +
      edge.startDisplayVec.x.toString() +
      ' ' +
      edge.startDisplayVec.y.toString() +
      ' A ' +
      radius.toString() +
      ' ' +
      radius.toString() +
      ' 0 ' +
      bigArc +
      ' 0 ' +
      edge.endDisplayVec.x.toString() +
      ' ' +
      edge.endDisplayVec.y.toString() +
      (forClipping ? ' Z' : '');

    const pathOptions = forClipping
      ? {}
      : {
          stroke: secondaryBorderColor,
          strokeWidth: EDGE_STROKE_WIDTH,
          fill: useOverlay ? secondaryBorderColor : 'transparent',
          opacity: useOverlay ? SELECTION_CIRCLE_OPACITY : 1,
          name: `${CANVAS_OBJECT_TYPE_PREFIX.RADIUS_OUT}_${edge.id}`,
        };

    return new fabric.Path(pathString, pathOptions);
  },

  addBumpOut: (
    edge: DrawingEdge,
    forClipping: boolean,
    useOverlay: boolean
  ): fabric.Path => {
    if (!edge.bumpOut) {
      throw new Error(`The edge ${edge} doesn't contain a bump out`);
    }

    const edgeLength = getDistanceBetween(
      edge.startDisplayVec,
      edge.endDisplayVec
    );
    const bumpOutLength = createBumpOutWithHalfEdgeLength(
      edge,
      edgeLength,
      edge.bumpOut?.length ?? DEFAULT_BUMPOUT_LENGTH
    );
    const bumpOutDepth = edge.bumpOut?.depth ?? DEFAULT_BUMPOUT_DEPTH;

    const bumpVec = buildBumpOutArrayOfPoints(
      edge,
      bumpOutLength,
      bumpOutDepth
    );

    const path = createBumpOutPath(edge, bumpVec, forClipping, useOverlay);

    if (!forClipping && edge.bumpOut?.depth) {
      const newBumpOut = Object.assign(
        { ...DEFAULT_BUMPOUT },
        {
          length: bumpOutLength,
          counterClockwiseRemainderLength: !edge.bumpOut
            .counterClockwiseRemainderLength
            ? roundToArrowLengthGranularity((edgeLength - bumpOutLength) / 2)
            : edge.bumpOut.counterClockwiseRemainderLength,
          clockwiseRemainderLength: !edge.bumpOut.clockwiseRemainderLength
            ? roundToArrowLengthGranularity((edgeLength - bumpOutLength) / 2)
            : edge.bumpOut.clockwiseRemainderLength,
          depth: edge.bumpOut?.depth,
          arrowsInfo: [],
        }
      );

      const mutableEdge = ensureMutable(edge);

      forceAssignProperty(mutableEdge, 'bumpOut', newBumpOut);
    }

    return path;
  },

  getLargestShapeDimension: (
    shapeWidthsAndHeights: number[],
    defaultLargestShapeDimension = DEFAULT_LARGEST_SHAPE_DIMENSION
  ): number =>
    shapeWidthsAndHeights.length
      ? Math.max(...shapeWidthsAndHeights)
      : defaultLargestShapeDimension,

  calcSlabScale: (drawingShapes: DrawingShape[]): number => {
    let slabScale = SLAB_SCALE.DEFAULT;

    if (drawingShapes.length < 2) {
      return slabScale;
    }

    // find largest dimension for any drawing shape
    const shapeWidthsAndHeights: number[] = [];

    drawingShapes.forEach((shape) => {
      if (shape.type !== ShapeType.FAUX_SHAPE) {
        const shapeBoundingRect = shape.fabricGroup?.getBoundingRect(
          true,
          true
        );

        // as slab images tend to be wider then tall, adjust the width in consideration of the scale
        shapeWidthsAndHeights.push(
          shapeBoundingRect.width * SLAB_SCALE.WIDTH_ADJUST,
          shapeBoundingRect.height
        );
      }
    });
    const largestShapeDimension = drawingTool.getLargestShapeDimension(
      shapeWidthsAndHeights
    );

    // set slab scale based on largest shape dimension
    if (largestShapeDimension > 300) {
      slabScale = SLAB_SCALE.MIN;
    } else {
      // polynomial formula (based on heuristics) to dynamically determine reasonable slab scale
      slabScale =
        SLAB_SCALE.POLYN_A * largestShapeDimension * largestShapeDimension +
        SLAB_SCALE.POLYN_B * largestShapeDimension +
        SLAB_SCALE.POLYN_C;
    }
    slabScale = Math.max(Math.min(slabScale, SLAB_SCALE.MAX), SLAB_SCALE.MIN);

    return slabScale;
  },

  fillColor: (
    canvas: fabric.Canvas,
    color: ColorModel,
    drawingShapes: DrawingShape[],
    useOverlay: boolean,
    imageElement: HTMLImageElement
  ): void => {
    const patternTransformation = calculatePatternTransformation(
      color,
      drawingShapes
    );

    canvas
      .getObjects()
      .filter(isShapeGroup)
      .flatMap((obj) => (obj as fabric.Group).getObjects())
      .forEach((item) => {
        if (isBacksplashGroup(item)) {
          applyFillToBacksplash(
            item as fabric.Group,
            imageElement,
            patternTransformation
          );
        } else if (isMainShape(item)) {
          const matchingShape = drawingShapes.find(
            (shape) => shape.canvasObjectName === item.name
          );

          if (matchingShape) {
            applyFillToMainShape(
              item,
              matchingShape,
              useOverlay,
              imageElement,
              patternTransformation
            );
          }
        }
      });

    //This render is needed as it is inside async module
    //but only needs to execute once outside the loop
    canvas.renderAll();
  },

  getOutlinePointsFromCorners: (corners: CornerInfo[], index: number) => {
    type XAxis = 'prev' | 'next';
    type YAxis = 'upp' | 'bot';

    const outlinePts = corners.reduce<(CornerInfo | [XAxis, YAxis])[]>(
      (points, corner) =>
        points.map((pt) => {
          if (!Array.isArray(pt)) {
            return pt;
          }
          const [x, y] = pt;
          const indexMod = index - (x === 'prev' ? 1 : 0);
          const isUpper = y === 'upp';

          return corner.skIndex === indexMod && corner.isUpper === isUpper
            ? corner
            : pt;
        }),
      [
        ['prev', 'upp'],
        ['next', 'upp'],
        ['next', 'bot'],
        ['prev', 'bot'],
      ]
    );

    return outlinePts.every((pt) => !Array.isArray(pt))
      ? (outlinePts as CornerInfo[])
      : undefined;
  },

  getClipGroup: (
    drawingShape: DrawingShape,
    useOverlay: boolean
  ): fabric.Group => {
    const { skeleton } = drawingShape;
    const clipShapeList: ClipShapeListType[] = [];

    for (let i = 1; i < skeleton.length; i++) {
      getBoneOutline(drawingShape, clipShapeList, useOverlay, i);
    }

    // overlays for radius and bump outs are handled during the non-clipping drawing functions
    if (!useOverlay) {
      drawingShape.drawingEdges
        .filter((edge) => edge.isVisibleInCanvas)
        .forEach((edge) => {
          if (isRadiusOut(edge)) {
            clipShapeList.push(drawingTool.addRadiusOut(edge, true, false));
          }
          if (isBumpOut(edge)) {
            clipShapeList.push(drawingTool.addBumpOut(edge, true, false));
          }
        });
    }

    return new fabric.Group(clipShapeList, {
      absolutePositioned: true,
    });
  },

  clearCanvas: ({
    canvas,
    preserveAppliances = false,
  }: {
    canvas: fabric.Canvas;
    preserveAppliances?: boolean;
  }): void => {
    if (!canvas) {
      return;
    }

    try {
      const isDisposed =
        (canvas as fabric.Canvas & { _disposed?: boolean })._disposed ||
        !canvas.getContext() ||
        !canvas.getWidth ||
        !canvas.getHeight;

      if (isDisposed) {
        console.warn('Canvas is disposed or invalid, skipping clear operation');

        return;
      }

      const context = canvas.getContext();

      if (!context) {
        console.warn('Canvas context is null, skipping clear operation');

        return;
      }

      if (
        typeof canvas.getWidth !== 'function' ||
        typeof canvas.getHeight !== 'function'
      ) {
        console.warn(
          'Canvas is not properly initialized, skipping clear operation'
        );

        return;
      }

      if (canvas.getWidth() <= 0 || canvas.getHeight() <= 0) {
        console.warn('Canvas has invalid dimensions, skipping clear operation');

        return;
      }

      try {
        const objects = canvas.getObjects();

        if (
          objects &&
          objects.length > 0 &&
          !(canvas as fabric.Canvas & { _disposed?: boolean })._disposed
        ) {
          if (preserveAppliances) {
            // Extract appliances from groups before removing groups
            // This allows us to preserve appliance images even when groups are removed
            const appliancesToPreserve: fabric.Image[] = [];

            // Find appliances directly on canvas
            objects.forEach((obj) => {
              if (
                obj.name?.includes(CANVAS_OBJECT_TYPE_PREFIX.APPLIANCE) &&
                obj.type === 'image'
              ) {
                appliancesToPreserve.push(obj as fabric.Image);
              }
            });

            // Extract appliances from shape groups
            objects.forEach((obj) => {
              if (isShapeGroup(obj)) {
                const group = obj as fabric.Group;

                group.getObjects().forEach((groupObj) => {
                  if (
                    groupObj.name?.includes(
                      CANVAS_OBJECT_TYPE_PREFIX.APPLIANCE
                    ) &&
                    groupObj.type === 'image'
                  ) {
                    // Remove from group and add to preserve list
                    group.remove(groupObj);
                    appliancesToPreserve.push(groupObj as fabric.Image);
                  }
                });
              }
            });

            // Remove all objects (groups will be recreated)
            canvas.remove(...objects);

            // Re-add preserved appliances directly to canvas
            // They'll be moved to appropriate groups by addAppliancesToCanvas
            appliancesToPreserve.forEach((appliance) => {
              canvas.add(appliance);
            });
          } else {
            // Remove all objects (original behavior)
            canvas.remove(...objects);
          }
        }
      } catch (error) {
        console.warn('Error removing canvas objects:', error);
      }
    } catch (error) {
      console.warn('Error clearing canvas:', error);
    }
  },

  addArrowInfoData: (shape: DrawingShape): void => {
    const outlinePointsForArrows = getOutlinePointsFromModifiedShape(
      shape,
      true
    );
    const outlineDiagonalFlags =
      getOutlineDiagonalFlagsFromModifiedShape(shape);
    const outlineEdges = getOutlineCustomSideEdges(shape);
    const outlinePointsForInteriorArrows = getOutlinePointsFromShape(shape);

    shape.highlightPoints = outlinePointsForArrows;
    const previousShapeValues = { ...shape };

    shape.arrowsInfo = getArrowsFromOutlines(
      outlinePointsForArrows,
      outlinePointsForInteriorArrows,
      outlineDiagonalFlags,
      outlineEdges
    );

    previousShapeValues.arrowsInfo?.forEach((oldArrow, index) => {
      const newArrowInfo = shape.arrowsInfo;

      if (shape.arrowsInfo && newArrowInfo && newArrowInfo[index]) {
        const currentArrowInfo: ArrowInfo = {
          ...newArrowInfo[index],
          label: oldArrow.label,
          drawingEdgeId: !oldArrow.isInterior
            ? oldArrow.drawingEdgeId
            : shape.arrowsInfo[index].drawingEdgeId,
          isVisibleInCanvas: oldArrow?.isVisibleInCanvas ?? true,
          isActiveForEdgeCalc: oldArrow?.isActiveForEdgeCalc ?? true,
          minimumLength: oldArrow.minimumLength,
          maximumLength: oldArrow.maximumLength,
        };

        if (shape.arrowsInfo && !currentArrowInfo.isDiagonalCorner) {
          shape.arrowsInfo[index] = currentArrowInfo;
        }
      }
    });
  },

  /**
   * Creates a group of arrows using a set of options
   * @param arrowInfo the arrow in form of a vector
   * @param options the set of options to build the group of arrows
   * @returns the created group of arrows
   */
  createArrowGroup: (
    arrowInfo: ArrowInfo,
    options: IObjectOptions
  ): fabric.Group | undefined => {
    if (!arrowInfo.isVisibleInCanvas) {
      return undefined;
    }

    const arrowPts = arrowInfo.path;
    const mainSegment = new fabric.Polyline(
      [arrowPts[0], arrowPts[1]],
      options
    );
    const leftArrowWing = new fabric.Polyline(
      [arrowPts[2], arrowPts[3], arrowPts[4]],
      options
    );
    const rightArrowWing = new fabric.Polyline(
      [arrowPts[5], arrowPts[6], arrowPts[7]],
      options
    );

    return new fabric.Group([mainSegment, leftArrowWing, rightArrowWing], {
      selectable: false,
    });
  },

  addMeasurements: (
    canvas: fabric.Canvas,
    shapes: DrawingShape[],
    color: string,
    onEdit: (
      editedArrow: ArrowInfo,
      drawingShape: DrawingShape,
      enteredMeasurementText?: number
    ) => void,
    activeDrawingShape?: DrawingShape
  ): void => {
    const opt: IObjectOptions = {
      stroke: stroke.normal,
      strokeWidth: MEASUREMENT_STROKE_WIDTH,
      fill: stroke.normal,
      selectable: false,
      data: {},
    };
    const interiorOpt = { ...opt, stroke: color, fill: color };

    shapes
      .filter((shape) => shape.type !== ShapeType.FAUX_SHAPE)
      .forEach((shape) => {
        drawingTool.addArrowInfoData(shape);
        shape.arrowsInfo?.forEach((arrowInfo) => {
          const [startPoint, endPoint] = arrowInfo.path;
          const group = drawingTool.createArrowGroup(
            arrowInfo,
            arrowInfo.isInterior ? interiorOpt : opt
          );

          if (group) {
            group.data = {
              parentName: shape?.canvasObjectName,
              highlightable: true,
              inInterior: arrowInfo.isInterior,
            };
            if (arrowInfo.isInterior) {
              group.name = `${CANVAS_OBJECT_TYPE_PREFIX.INTERIOR_ARROW}`;
              canvas.add(group);
            } else {
              shape.fabricGroup.addWithUpdate(group);
            }
          }

          drawingTool.addMeasureText(
            shape,
            canvas,
            { startPoint, endPoint },
            arrowInfo.label,
            arrowInfo,
            color,
            onEdit,
            activeDrawingShape
          );
        });
      });
  },

  getMeasurementPosition: (
    arrow: LineSegment,
    text: fabric.Text,
    normalVec: Vec2
  ): Vec2 => {
    const pos = arrow.startPoint.add(arrow.endPoint, true).multiply(0.5);
    const boundRc = text.getBoundingRect();
    const direction = getDirection(arrow);

    let sign = 1;

    switch (direction) {
      case LineDirection.TOP:
        pos.add(
          normalVec.multiply(OFFSET_MEASUREMENT_TEXT + boundRc.height, true)
        );
        pos.set(pos.x - boundRc.width * 0.5, pos.y, false);
        break;
      case LineDirection.BOTTOM:
        pos.add(normalVec.multiply(OFFSET_MEASUREMENT_TEXT, true));
        pos.set(pos.x - boundRc.width * 0.5, pos.y, false);
        break;
      case LineDirection.VERTICAL_LEFT:
        pos.set(
          pos.x - boundRc.width - OFFSET_MEASUREMENT,
          pos.y - boundRc.height * 0.5,
          false
        );
        break;
      case LineDirection.VERTICAL_RIGHT:
        pos.set(
          pos.x + OFFSET_MEASUREMENT + LABEL_OFFSET,
          pos.y - boundRc.height * 0.5,
          false
        );
        break;
      case LineDirection.LEFT:
        sign = normalVec.y < 0 ? -1 : 0;
        pos.set(pos.x - boundRc.width, pos.y + sign * boundRc.height, false);
        break;
      default:
        sign = normalVec.y < 0 ? -1 : 0;
        pos.set(pos.x, pos.y + sign * boundRc.height, false);
        break;
    }

    //Pos refinement
    if (
      sign === 0 &&
      (direction === LineDirection.LEFT || direction === LineDirection.RIGHT)
    ) {
      pos.set(pos.x, pos.y + OFFSET_MEASUREMENT, false);
    }

    return pos;
  },
  /**
   * Adds edge label and editable length attribute.
   * On edit callback is called when the user has finished editing the length.
   */
  addMeasureText: (
    shape: DrawingShape,
    canvas: fabric.Canvas,
    arrow: LineSegment,
    label: string,
    arrowInfo: ArrowInfo,
    color: string,
    onEdit: (editedArrow: ArrowInfo, drawingShape: DrawingShape) => void,
    activeDrawingShape?: DrawingShape
  ): void => {
    arrowInfo.label = label;
    if (!arrowInfo.isVisibleInCanvas) {
      return;
    }
    shape.editableLabels.push(
      new EditableLengthLabel(
        shape,
        arrow,
        arrowInfo,
        label,
        onEdit,
        canvas,
        color,
        activeDrawingShape
      )
    );
  },

  positionShapeLabel: (shape: DrawingShape): Vec2 => {
    const requiredEdgeLength = ROUGH_ESTIMATE_FOR_SHAPE_LABEL_LENGTH;

    const { shapeTextPos, longestHorizontalTopEdge } =
      determineShapeLabelPosition(shape, requiredEdgeLength);

    return (
      shapeTextPos ||
      getShapeTextPos(shape, longestHorizontalTopEdge, requiredEdgeLength)
    );
  },

  nextAvailableShapeNumber: (shapes: DrawingShape[]): number =>
    Array.from({ length: shapes.length + 1 }, (_, index) => index + 1).find(
      (shapeNum) => !shapes.some((shape) => shape.label === `Shape ${shapeNum}`)
    ) || 0,

  addShapeText: (
    canvas: fabric.Canvas,
    shapes: DrawingShape[],
    color: string
  ): DrawingShape[] =>
    shapes.map((shape, index) => {
      if (shape.type === ShapeType.FAUX_SHAPE || !isSomething(shape)) {
        return shape;
      }

      const shapeLabel: string =
        shape?.label || `Shape ${drawingTool.nextAvailableShapeNumber(shapes)}`;
      const shapeTextPos = drawingTool.positionShapeLabel(shape);

      // Ensure consistent fontSize regardless of canvas zoom level
      const normalizedFontSize = 5;

      const shapeNameText = new fabric.IText(shapeLabel, {
        fontFamily,
        stroke: color,
        fill: color,
        height: 100,
        strokeWidth: LABEL_STROKE_WIDTH,
        left: shapeTextPos?.x + SHAPE_LABEL_MARGIN,
        top: shapeTextPos?.y + SHAPE_LABEL_MARGIN,
        editable: true,
        fontSize: normalizedFontSize,
        hoverCursor: 'pointer',
        hasControls: false,
        lockMovementX: true,
        lockMovementY: true,
        name: `${CANVAS_OBJECT_TYPE_PREFIX.SHAPE_LABEL}_${shapeLabel}`,
        data: {
          parentName: shape?.canvasObjectName,
          highlightable: true,
          inInterior: true,
        },
      });

      shape.label = shapeLabel;
      shape.counterLabel = index + 1;
      shape.onRename = new Subject<string>();

      shapeNameText.on('editing:entered', () => {
        shapeNameText?.hiddenTextarea?.setAttribute(
          'maxlength',
          SHAPE_NAME_MAX_LENGTH.toString()
        );
      });
      shapeNameText.onKeyDown = (e: KeyboardEvent) => {
        if (e.key === KEYS.ENTER || e.key === KEYS.TAB) {
          shapeNameText.exitEditing();
        }
      };
      shapeNameText.on('editing:exited', () => {
        const text = shapeNameText.text?.trim() || shapeLabel;

        shapeNameText.set('text', text);
        shape.onRename?.next(text);
      });

      canvas.add(shapeNameText);

      drawingTool.replaceITextWithEllipsisIfNeeded(
        shapeNameText,
        getOutlinePointsFromShape(shape)
      );

      return shape;
    }),
  replaceITextWithEllipsisIfNeeded: (
    label: fabric.Text,
    currShapePolygon: Vec2[]
  ): void => {
    if (hasRequiredProps(label, ['aCoords', 'width', 'height'])) {
      const shapeLabelPolygon = [];

      shapeLabelPolygon.push(
        new Vec2(
          label.aCoords.tl.x + LABEL_LEFT_COLLISION_MARGIN,
          label.aCoords.tl.y
        )
      );
      shapeLabelPolygon.push(
        new Vec2(
          label.aCoords.tl.x + label.width + LABEL_LEFT_COLLISION_MARGIN,
          label.aCoords.tl.y
        )
      );
      shapeLabelPolygon.push(
        new Vec2(
          label.aCoords.tl.x + label.width + LABEL_LEFT_COLLISION_MARGIN,
          label.aCoords.tl.y + label.height
        )
      );
      shapeLabelPolygon.push(
        new Vec2(
          label.aCoords.tl.x + LABEL_LEFT_COLLISION_MARGIN,
          label.aCoords.tl.y + label.height
        )
      );

      const result = polygonCollisions(shapeLabelPolygon, [currShapePolygon]);

      if (result[0]) {
        const labelLength = label.text?.length;

        if (labelLength && labelLength > LABEL_ELLIPSIS_LIMIT) {
          label.set(
            'text',
            label.text?.slice(0, label.text?.length - LABEL_ELLIPSIS_LIMIT) +
              ELLIPSIS
          );
          drawingTool.replaceITextWithEllipsisIfNeeded(label, currShapePolygon);
        }
      }
    }
  },

  rotateAndPositionSideItemLabel: (
    label: fabric.Text,
    directionVector: Vec2
  ): void => {
    const dirVec = directionVector;

    const angleBetweenVectAndX = Math.atan2(dirVec.y, dirVec.x);
    let convertedAngle = convertFromRadiansToDegree(angleBetweenVectAndX);

    if (convertedAngle <= -90) {
      convertedAngle += 180;
    } else if (convertedAngle > 90) {
      convertedAngle -= 180;
    }

    // adjust the label position based on the required rotation (EE-966)
    const adjY = 1.7;
    const adjX = 8;
    const cos = Math.cos((convertedAngle * Math.PI) / 180);
    const sin = Math.sin((convertedAngle * Math.PI) / 180);

    if (label.top && label.left) {
      label.top -= adjY * cos + adjX * sin;
      label.left -= adjX * cos - adjY * sin;
    }

    // now actually do the rotation
    label.centeredRotation = false; // it's way off without this line
    label.rotate(convertedAngle);
  },

  addRectangleSideItemArrowInfoMeasurement: (
    _shape: DrawingShape,
    arrow: LineSegment,
    arrowInfo: ArrowInfo
  ): fabric.IText => {
    const diffVec = subtractVectors(arrow.endPoint, arrow.startPoint);
    const normalVec = diffVec.rotate(Math.PI / 2, undefined, true).normalize();

    arrowInfo.size = roundToArrowLengthGranularity(diffVec.length());
    const text = new fabric.IText('', {
      stroke: stroke.normal,
      fill: stroke.normal,
      strokeWidth: MEASUREMENT_STROKE_WIDTH,
      fontSize: 2.5,
      fontFamily,
      selectable: false,
      name: CANVAS_OBJECT_TYPE_PREFIX.BACKSPLASH_MEASURE_LABEL + uniqueId(),
    });

    // get position of the measurements
    const pos = drawingTool.getMeasurementPosition(arrow, text, normalVec);

    text.left = pos.x;
    text.top = pos.y;

    return text;
  },

  addRectangleSideItemArrows: (
    canvas: fabric.Canvas,
    shape: DrawingShape,
    outlinePoints: Vec2[],
    drawingEdge: DrawingEdge,
    onEdit: (editedArrow: ArrowInfo, shape: DrawingShape) => void,
    activeDrawingShape?: DrawingShape
  ): fabric.Group | undefined => {
    const arrowOpt: IObjectOptions = {
      stroke: stroke.normal,
      strokeWidth: MEASUREMENT_STROKE_WIDTH,
      fill: stroke.normal,
      selectable: false,
      data: {},
    };

    const removedArrowInfoLabel = shape.arrowsInfo?.find(
      (arrow) =>
        arrow.drawingEdgeId === drawingEdge.id && !arrow.isVisibleInCanvas
    )?.label;

    drawingEdge.sideItem.arrowsInfo = getRectangleSideItemArrowsFromOutlines(
      outlinePoints,
      removedArrowInfoLabel
    ).map((arrow) => ({
      path: arrow.path,
      label: arrow.label,
      isVisibleInCanvas: true,
    }));

    let arrowsGroup;

    drawingEdge.sideItem.arrowsInfo.forEach((arrowInfo, index) => {
      drawingTool.removeObjectByName(
        canvas,
        `${CANVAS_OBJECT_TYPE_PREFIX.BACKSPLASH_ARROW}_${index}`,
        true
      );

      arrowsGroup = createArrowsGroup(arrowInfo, arrowOpt);

      // add labels for each arrow
      const arrowPts = arrowInfo.path;
      const groupCenterPoint = shape.fabricGroup.getCenterPoint();
      const groupCenterVec2 = new Vec2(groupCenterPoint.x, groupCenterPoint.y);
      const arrowPtsAbsolute = [
        arrowPts[0].add(groupCenterVec2, true),
        arrowPts[1].add(groupCenterVec2, true),
      ];
      const lineSegmentAbsolute = {
        startPoint: arrowPtsAbsolute[0],
        endPoint: arrowPtsAbsolute[1],
      };
      const lineSegment = {
        startPoint: arrowPts[0],
        endPoint: arrowPts[1],
      };

      arrowsGroup.data = {
        parentName: shape.canvasObjectName,
        highlightable: true,
        name: `${CANVAS_OBJECT_TYPE_PREFIX.BACKSPLASH_ARROW}_${index}`,
      };
      if (arrowInfo.label?.length !== 0) {
        // Shape lengths can only be written by changing the old side length
        const oldArrowInfo = findArrowByEdgeId(drawingEdge.id, shape);

        if (!oldArrowInfo?.label) {
          return;
        }
        shape.editableLabels.push(
          new EditableLengthLabel(
            shape,
            lineSegmentAbsolute,
            oldArrowInfo,
            oldArrowInfo.label,
            onEdit,
            canvas,
            BLACK,
            activeDrawingShape
          )
        );
        shape.fabricGroup.add(arrowsGroup);

        const diffVec = lineSegment.endPoint.subtract(
          lineSegment.startPoint,
          true
        );

        arrowInfo.size = roundToArrowLengthGranularity(diffVec.length());
      } else {
        arrowInfo.isSideItemPresent = true;
        arrowInfo.drawingEdgeId = drawingEdge.id;

        const measurementText =
          drawingTool.addRectangleSideItemArrowInfoMeasurement(
            shape,
            lineSegment,
            arrowInfo
          );

        shape.editableLabels.push(
          new EditableLengthLabel(
            shape,
            lineSegmentAbsolute,
            arrowInfo,
            arrowInfo.label,
            onEdit,
            canvas,
            BLACK,
            activeDrawingShape
          )
        );

        shape.fabricGroup.add(arrowsGroup, measurementText);
      }
    });

    return arrowsGroup;
  },

  drawRectangleSideItem: (
    canvas: fabric.Canvas,
    shape: DrawingShape,
    corners: CornerInfo[],
    drawingEdge: DrawingEdge,
    depth: number,
    selectedColor: ColorModel,
    onEdgeEdit: (editedArrow: ArrowInfo, shape: DrawingShape) => void,
    label: string,
    activeDrawingShape?: DrawingShape
  ) => {
    if (!depth || !corners || !shape) {
      return;
    }

    drawingTool.removeObjectByName(
      canvas,
      `${CANVAS_OBJECT_TYPE_PREFIX.BACKSPLASH_GROUP}_${drawingEdge.id}`,
      true
    );

    const optSideItem: IObjectOptions = {
      fill: 'transparent',
      selectable: false,
      stroke: borderColor,
      strokeWidth: GREY_STROKE_WIDTH_BACKSPLASH,
      strokeUniform: true,
      name: CANVAS_OBJECT_TYPE_PREFIX.POLYGON_BACKSPLASH + uniqueId(),
    };

    optSideItem.data = {
      parentName: shape?.canvasObjectName,
    };

    // restore applied Corners
    const cornerRadiiInfo = Object.values({ ...shape.cornerMap });

    // EE-968 ensure corners are sorted by filtering each individually (helps with side item arrow display)
    corners = [
      ...cornerRadiiInfo.filter(({ id }) => id === drawingEdge.startCornerId),
      ...cornerRadiiInfo.filter(({ id }) => id === drawingEdge.endCornerId),
    ];

    // create side item from removed drawing edge corner

    const dirVec = subtractVectors(corners[1].coords, corners[0].coords);
    const normalVec = dirVec.rotate(Math.PI / 2, undefined, true).normalize();
    const lbPt = corners[0].coords.add(
      normalVec.multiply(OFFSET_BACKSPLASH, true),
      true
    );
    const rbPt = corners[1].coords.add(
      normalVec.multiply(OFFSET_BACKSPLASH, true),
      true
    );
    const ltPt = lbPt.add(normalVec.multiply(depth, true), true);
    const rtPt = rbPt.add(normalVec.multiply(depth, true), true);

    const sideItemOutlinePoints = [ltPt, rtPt, rbPt, lbPt]; // EE-968 no need to convert order, already sorted

    // at this point, the coords are relative to the group object, so this must be subtracted for absolute coords
    const ltPtCopy = new Vec2(ltPt.x, ltPt.y);
    const rtPtCopy = new Vec2(rtPt.x, rtPt.y);
    const rbPtCopy = new Vec2(rbPt.x, rbPt.y);
    const lbPtCopy = new Vec2(lbPt.x, lbPt.y);
    const groupCenterPoint = shape.fabricGroup.getCenterPoint();
    const groupCenterVec2 = new Vec2(groupCenterPoint.x, groupCenterPoint.y);
    const sideItemOutlinePointsAbsolute = [
      ltPtCopy.subtract(groupCenterVec2),
      rtPtCopy.subtract(groupCenterVec2),
      rbPtCopy.subtract(groupCenterVec2),
      lbPtCopy.subtract(groupCenterVec2),
    ];

    const sideItemPolygon = new fabric.Polygon(
      sideItemOutlinePoints,
      optSideItem
    );

    // add arrows object to side item
    const sideItemArrows = drawingTool.addRectangleSideItemArrows(
      canvas,
      shape,
      sideItemOutlinePointsAbsolute,
      drawingEdge,
      onEdgeEdit,
      activeDrawingShape
    );

    // add side item label
    const midPoint = getMidpoint(ltPt, rbPt); // only one midpoint needed (EE-966) //
    const sideItemLabel = new fabric.IText(label, {
      fontFamily,
      stroke: selectedColor.overlayColor,
      fill: selectedColor.overlayColor,
      left: midPoint.x - 0.25,
      top: midPoint.y - 0.25,
      height: 50,
      strokeWidth: LABEL_STROKE_WIDTH,
      textAlign: 'center',
      editable: false,
      fontSize: 3,
      hoverCursor: 'pointer',
      hasControls: false,
      lockMovementX: true,
      lockMovementY: true,
      name: `${CANVAS_OBJECT_TYPE_PREFIX.BACKSPLASH_LABEL}`,
      data: {
        parentName: shape?.canvasObjectName,
        highlightable: true,
        inInterior: true,
      },
    });

    drawingTool.rotateAndPositionSideItemLabel(sideItemLabel, dirVec);

    if (sideItemArrows) {
      // add side item group
      const sideItemGroup = new fabric.Group([sideItemPolygon, sideItemLabel], {
        selectable: false,
      });

      sideItemGroup.name = `${CANVAS_OBJECT_TYPE_PREFIX.BACKSPLASH_GROUP}_${drawingEdge.id}`;
      shape.fabricGroup.addWithUpdate(sideItemGroup);
    }

    canvas.renderAll();
  },

  /**
   * Generates the bump out outline points for a given drawing edge.
   *
   * @param {DrawingEdge} drawingEdge - The edge of the drawing containing the bump out data.
   * @returns {Vec2[]} An array of `Vec2` points representing the bump out outline.
   */
  getBumpOutOutline: (drawingEdge: DrawingEdge): Vec2[] => {
    const bumpOutOutline: Vec2[] = [];

    if (drawingEdge.bumpOut) {
      bumpOutOutline.push(drawingEdge.startDisplayVec);
      bumpOutOutline.push(
        bumpOutOutline[0].add(
          drawingEdge.dirVec
            .normalize(true)
            .multiply(drawingEdge.bumpOut?.clockwiseRemainderLength, true),
          true
        )
      );
      bumpOutOutline.push(
        bumpOutOutline[1].add(
          drawingEdge.normalVec.multiply(drawingEdge.bumpOut?.depth, true),
          true
        )
      );
      bumpOutOutline.push(
        bumpOutOutline[2].add(
          drawingEdge.dirVec
            .normalize(true)
            .multiply(drawingEdge.bumpOut.length, true),
          true
        )
      );
      bumpOutOutline.push(
        bumpOutOutline[3].subtract(
          drawingEdge.normalVec.multiply(drawingEdge.bumpOut.depth, true),
          true
        )
      );
      bumpOutOutline.push(drawingEdge.endDisplayVec);

      return bumpOutOutline;
    }

    return bumpOutOutline;
  },

  /**
   * Adds bump outs to a list of shape edges
   * @param canvas the canvas object
   * @param shape the shape to add the bump out arrows
   * @param drawingEdge the drawing edge to apply the bump out
   * @param onEdit the onEdit function
   * @param activeDrawingShape the currently selected drawing shape
   * @returns the list of objects that were affected by the bump outs
   */
  addBumpOutArrows: (
    canvas: fabric.Canvas,
    shape: DrawingShape,
    drawingEdge: DrawingEdge,
    onEdit: (editedArrow: ArrowInfo, shape: DrawingShape) => void,
    activeDrawingShape?: DrawingShape
  ): void => {
    const groupCenterPoint = shape.fabricGroup.getCenterPoint();
    const groupCenterVec2 = new Vec2(groupCenterPoint.x, groupCenterPoint.y);

    if (drawingEdge.bumpOut) {
      if (drawingEdge.bumpOut.arrowsInfo.length > 0) {
        const emptyArrowsInfo: ArrowInfo[] = [];

        const mutableBumpOut = ensureMutable(drawingEdge.bumpOut);

        forceAssignProperty(mutableBumpOut, 'arrowsInfo', emptyArrowsInfo);

        if (mutableBumpOut !== drawingEdge.bumpOut) {
          const mutableEdge = ensureMutable(drawingEdge);

          forceAssignProperty(mutableEdge, 'bumpOut', mutableBumpOut);
        }
      }

      const bumpOutOutline = drawingTool.getBumpOutOutline(drawingEdge);
      const bumpOutOutlineAbsolute = bumpOutOutline.map((corner) =>
        corner.subtract(groupCenterVec2, true)
      );
      const bumpOutArrows = getBumpOutArrowsFromOutlines(
        bumpOutOutlineAbsolute
      );

      const middleOfBumpOut = drawingEdge.startDisplayVec
        .add(
          drawingEdge.dirVec
            .normalize(true)
            .multiply(drawingEdge.bumpOut.clockwiseRemainderLength, true),
          true
        )
        .add(
          drawingEdge.dirVec
            .normalize(true)
            .multiply(drawingEdge.bumpOut.length / 2, true),
          true
        );

      const distanceFromBumpOutMiddleToStart = getDistanceBetween(
        middleOfBumpOut,
        drawingEdge.startDisplayVec
      );
      const distanceFromBumpOutMiddleToEnd = getDistanceBetween(
        middleOfBumpOut,
        drawingEdge.endDisplayVec
      );

      const bumpOutLengthMax =
        (Math.min(
          distanceFromBumpOutMiddleToStart,
          distanceFromBumpOutMiddleToEnd
        ) -
          EDGE_LENGTH_INPUT_LIMIT.MIN) *
        2;

      // determine constraints for bumpOut arrows
      const displayableEdgeLength = getDistanceBetween(
        drawingEdge.startDisplayVec,
        drawingEdge.endDisplayVec
      );

      const remainderSideMax =
        displayableEdgeLength -
        drawingEdge.bumpOut.length -
        EDGE_LENGTH_INPUT_LIMIT.MIN;

      const newArrowsInfo = bumpOutArrows.map(
        (arrow, index) =>
          ({
            path: arrow.path,
            label: arrow.label,
            isVisibleInCanvas: true,
            drawingEdgeId: drawingEdge.id + '-' + index.toString(),
            isInterior: true,
            isDiagonalCorner: false,
            isActiveForEdgeCalc: false,
            maximumLength:
              index === BUMPOUT_LENGTH ? bumpOutLengthMax : remainderSideMax,
            minimumLength: EDGE_LENGTH_INPUT_LIMIT.MIN,
          }) as ArrowInfo
      );

      const mutableBumpOut = ensureMutable(drawingEdge.bumpOut);

      forceAssignProperty(mutableBumpOut, 'arrowsInfo', newArrowsInfo);

      if (mutableBumpOut !== drawingEdge.bumpOut) {
        const mutableEdge = ensureMutable(drawingEdge);

        forceAssignProperty(mutableEdge, 'bumpOut', mutableBumpOut);
      }
    }

    let bumpOutArrowsGroup;

    const arrowOpt: IObjectOptions = {
      stroke: stroke.normal,
      strokeWidth: MEASUREMENT_STROKE_WIDTH,
      fill: stroke.normal,
      selectable: false,
      data: {},
    };

    drawingEdge?.bumpOut?.arrowsInfo?.forEach((arrowInfo, index) => {
      drawingTool.removeObjectByName(
        canvas,
        `${CANVAS_OBJECT_TYPE_PREFIX.BUMPOUT_ARROW}_${index}`,
        true
      );

      // add labels for each arrow
      const arrowPts = arrowInfo.path;

      const arrowPtsAbsolute = [
        arrowPts[0].add(groupCenterVec2, true),
        arrowPts[1].add(groupCenterVec2, true),
      ];
      const lineSegmentAbsolute = {
        startPoint: arrowPtsAbsolute[0],
        endPoint: arrowPtsAbsolute[1],
      };

      bumpOutArrowsGroup = createArrowsGroup(arrowInfo, arrowOpt);

      bumpOutArrowsGroup.data = {
        parentName: shape.canvasObjectName,
        highlightable: true,
        name: `${CANVAS_OBJECT_TYPE_PREFIX.BUMPOUT_ARROW}_${index}`,
      };

      const newLabel = '';
      const newSize = roundToArrowLengthGranularity(
        getDistanceBetween(arrowPtsAbsolute[0], arrowPtsAbsolute[1])
      );

      const mutableArrowInfo = ensureMutable(arrowInfo);

      forceAssignProperty(mutableArrowInfo, 'label', newLabel);
      forceAssignProperty(mutableArrowInfo, 'size', newSize);

      shape.editableLabels.push(
        new EditableLengthLabel(
          shape,
          lineSegmentAbsolute,
          arrowInfo,
          '',
          onEdit,
          canvas,
          BLACK,
          activeDrawingShape
        )
      );

      shape.fabricGroup.add(bumpOutArrowsGroup);
    });
  },

  /**
   * Moves a group of canvas objects to the top of the stack of drawn objects
   * @param canvas the canvas object
   */
  bringAllLabelsForward: (canvas: fabric.Canvas): void => {
    canvas.getObjects().forEach((target) => {
      if (
        hasShapeMeasureLabel(target) ||
        isShapeLabel(target) ||
        hasEdgeName(target)
      ) {
        canvas.bringToFront(target);
      }
    });
  },

  /**
   * Selects a shape and applies a color stroke based on different conditions
   * @param allShapes the list of available shapes
   * @param canvas the canvas object
   * @param color the stroke color
   * @param activeDrawingShape the current selected shape
   */
  selectShape: ({
    allShapes,
    canvas,
    color,
    activeDrawingShape,
  }: {
    allShapes: DrawingShape[];
    canvas: fabric.Canvas;
    color: string;
    activeDrawingShape?: DrawingShape;
  }): void => {
    drawingTool.removeObjectByName(
      canvas,
      CANVAS_OBJECT_TYPE_PREFIX.ACTIVE_SHAPE_HIGHLIGHTS,
      true
    );

    allShapes.forEach((shape) => {
      lightenAllLabels(shape.editableLabels);
    });

    if (activeDrawingShape) {
      darkenAllLabels(activeDrawingShape.editableLabels);
    } else {
      allShapes.forEach((shape) => {
        removeLabelLetters(shape.editableLabels);
      });
    }

    setShapeNameAndColors(canvas, color, activeDrawingShape);

    allShapes.forEach(processShapeElements(activeDrawingShape));

    canvas.renderAll();
  },

  /**
   * Returns the labels of a given shape
   * @param canvas the canvas object
   * @returns the labels bound to the canvas
   */
  getShapeLabels: (canvas: fabric.Canvas): Array<fabric.Object> =>
    canvas
      .getObjects()
      .filter((obj) =>
        obj.name
          ?.toLocaleLowerCase()
          .includes(CANVAS_OBJECT_TYPE_PREFIX.SHAPE_LABEL)
      ),

  /**
   * Adds a text to the edge of a canvas object
   * @param canvas the canvas object
   * @param text the text for the edge
   * @param color the edge fill color
   * @param drawingShapes the list of shapes
   */
  addEdgeName: (
    canvas: fabric.Canvas,
    text: string,
    color: string,
    drawingShapes: DrawingShape[]
  ): void => {
    const shapeLabels = drawingTool.getShapeLabels(canvas);

    if (shapeLabels.length === 0) {
      return;
    }

    drawingTool.removeObjectByName(canvas, CANVAS_OBJECT_TYPE_PREFIX.EDGE_NAME);

    const matchingTargets = (label: fabric.Object) =>
      canvas
        .getObjects()
        .filter((target) =>
          target.name?.includes(
            `${label.data?.parentName}${CANVAS_OBJECT_TYPE_SUFFIX.SHAPE_GROUP}`
          )
        );

    const findDrawingShape = (parentName: string) =>
      drawingShapes.find((shape) => shape.canvasObjectName === parentName);

    const createEdgeText = (label: fabric.Object, edgeLabel: string) => {
      const parsedShapeName = label.name?.replace(
        `${CANVAS_OBJECT_TYPE_PREFIX.SHAPE_LABEL}`,
        ''
      );
      const edgeText = new fabric.Text(edgeLabel, {
        stroke: color,
        fill: color,
        strokeWidth: MEASUREMENT_STROKE_WIDTH,
        fontSize: 3.5,
        fontFamily: 'Helvetica',
        selectable: false,
        name: `${CANVAS_OBJECT_TYPE_PREFIX.EDGE_NAME}${parsedShapeName}`,
        data: {
          parentName: label.data?.parentName,
          highlightable: true,
          inInterior: true,
        },
      });

      if (label.aCoords && label.height) {
        edgeText.top = label.aCoords.tl.y + label.height;
        edgeText.left = label.aCoords.tl.x;
      }

      return edgeText;
    };

    const processLabel = (label: fabric.Object) => {
      const drawingShape = findDrawingShape(label.data?.parentName || '');

      if (!drawingShape) {
        return;
      }

      const edgeLabel = drawingShape.edge?.name || text;
      const edgeText = createEdgeText(label, edgeLabel);

      canvas.add(edgeText);
      drawingTool.replaceITextWithEllipsisIfNeeded(
        edgeText,
        getOutlinePointsFromShape(drawingShape)
      );
    };

    shapeLabels.forEach((label) =>
      matchingTargets(label).forEach(() => processLabel(label))
    );
  },

  /**
   * Populate cornerRadiusInfo array with initial values
   * @param canvas the fabric.Canvas object
   * @param shapes the list of shapes
   * @param color the color for the editable label
   * @param onEdit the edit function
   * @param cornerRadii the list of corner radius items
   */
  addCornerRadiiMeasurements: (
    canvas: fabric.Canvas,
    shapes: DrawingShape[],
    color: string,
    onEdit: EditableCornerLabelHandler
  ): void => {
    shapes.forEach((shape) => {
      Object.values(shape?.cornerMap).forEach((corner, index) => {
        corner.size = corner.size ?? DEFAULT_CORNER_RADIUS;

        if (corner.productId === undefined) {
          corner.label = formatCornerRadiusLabel(corner.size);
        }
        if (!corner.isVisibleInCanvas || corner.diagonalLength) {
          return;
        }
        shape.editableLabels.push(
          new EditableCornerLabel(
            shape,
            corner,
            CORNER_RADIUS_SIGN,
            onEdit,
            canvas,
            color,
            index
          )
        );
      });
    });
  },

  createCircleOnCorner:
    (
      parentDrawingShape: DrawingShape,
      selectedOption: CornerRadiusModel | DiagonalCornerModel,
      identifierName = CANVAS_OBJECT_TYPE_PREFIX.SELECTION_CIRCLE
    ) =>
    (radiusInfo: CornerInfo): fabric.Circle => {
      const circleName = isCornerRadiusModel(selectedOption)
        ? SELECTION_CIRCLES.FOR_ROUNDED_RADII
        : SELECTION_CIRCLES.FOR_DIAGONAL_CORNERS;

      return new fabric.Circle({
        left: radiusInfo.coords.x,
        top: radiusInfo.coords.y,
        radius: SELECTION_CIRCLE_RADIUS,
        fill: secondaryBorderColor,
        opacity: SELECTION_CIRCLE_OPACITY,
        originX: 'center',
        originY: 'center',
        hasControls: false,
        hasBorders: false,
        selectable: false,
        name: `${identifierName}_${circleName}`,
        data: {
          parentDrawingShape,
          cornerId: radiusInfo.id,
          selectedOption,
        },
      });
    },

  createCircleOnEdge:
    (parentDrawingShape: DrawingShape, selectedOption: CustomSideModel) =>
    (edgeInfo: DrawingEdge): fabric.Circle => {
      let edgeMidPoint = getMidpoint(
        edgeInfo.startDisplayVec,
        edgeInfo.endDisplayVec
      );
      let depth = 0;

      if (isRadiusOut(edgeInfo) && edgeInfo.radiusSagitta) {
        depth = edgeInfo.radiusSagitta;
      } else if (isBumpOut(edgeInfo) && edgeInfo.bumpOut) {
        edgeMidPoint = edgeInfo.startDisplayVec
          .add(
            edgeInfo.dirVec
              .normalize(true)
              .multiply(edgeInfo.bumpOut.clockwiseRemainderLength, true),
            true
          )
          .add(
            edgeInfo.dirVec
              .normalize(true)
              .multiply(edgeInfo.bumpOut.length / 2, true),
            true
          );
        depth = edgeInfo.bumpOut.depth;
      }

      if (depth) {
        edgeMidPoint.add(edgeInfo.normalVec.multiply(depth, true), false);
      }

      return new fabric.Circle({
        left: edgeMidPoint.x,
        top: edgeMidPoint.y,
        radius: SELECTION_CIRCLE_RADIUS,
        fill: secondaryBorderColor,
        opacity: SELECTION_CIRCLE_OPACITY,
        originX: 'center',
        originY: 'center',
        hasControls: false,
        hasBorders: false,
        selectable: false,
        name:
          CANVAS_OBJECT_TYPE_PREFIX.SELECTION_CIRCLE +
          '_' +
          SELECTION_CIRCLES.FOR_CUSTOM_SIDES,
        data: {
          parentDrawingShape,
          edgeId: edgeInfo.id,
          selectedOption,
        },
      });
    },

  canAddSelectionCirclesToShape:
    (
      selectedOptionId: number,
      selectedOptionRadius: RadiusRangeValues,
      selectedOptionDiagonalType: DiagonalCornerOptions,
      isSelectedOptionACornerRadius: boolean,
      removalCorner?: CornerRadiusModel | DiagonalCornerModel
    ) =>
    (shapeCorner: CornerInfo): boolean => {
      if (!shapeCorner.isVisibleInCanvas) {
        return false;
      }

      const isShapeCornerSameAsSelectedOption =
        shapeCorner.productId === selectedOptionId;

      if (removalCorner) {
        return isShapeCornerSameAsSelectedOption;
      }

      if (isSelectedOptionACornerRadius) {
        const isReplacingDiagonalWithRadius =
          (shapeCorner.diagonalLength ?? 0) > 0;
        const isReplacingRadiusWithAnother =
          shapeCorner.size !== selectedOptionRadius.max;
        const isReplacing =
          isReplacingDiagonalWithRadius || isReplacingRadiusWithAnother;
        const maxApplicableRadius = maxAvailableForCornerOptions(
          shapeCorner,
          selectedOptionRadius.min
        );
        const canApplyRadius =
          !shapeCorner.maxAllowedRadius ||
          shapeCorner.maxAllowedRadius >= maxApplicableRadius;

        return (
          !isShapeCornerSameAsSelectedOption && isReplacing && canApplyRadius
        );
      } /* is a diagonal corner */ else if (!shapeCorner.diagonalLength) {
        const angle = shapeCorner.angle ?? 0;
        const canApplyDiagonal: Record<DiagonalCornerOptions, boolean> = {
          [DiagonalCornerOptions.INSIDE_CORNER]: angle < 0,
          [DiagonalCornerOptions.OUTSIDE_CORNER]: angle > 0,
          [DiagonalCornerOptions.REMOVE_DIAGONAL_CORNER]: false,
        };

        return canApplyDiagonal[selectedOptionDiagonalType];
      }

      return false;
    },

  addCirclesToOutlinePointsOfShape: (
    canvas: fabric.Canvas,
    parentDrawingShape: DrawingShape,
    /** The selected corner radius/diagonal corner that the users intends to add or remove from the drawing */
    selectedOption: CornerRadiusModel | DiagonalCornerModel,
    selectedOptionDiagonalType: DiagonalCornerOptions,
    /** Additional options. TODO: move other function args to this object arg */
    options?: {
      removalCorner?: CornerRadiusModel | DiagonalCornerModel;
    }
  ): void => {
    const isCornerRadius = isCornerRadiusModel(selectedOption);

    Object.values(parentDrawingShape.cornerMap)
      .filter(
        drawingTool.canAddSelectionCirclesToShape(
          selectedOption.productId,
          isCornerRadius
            ? getMinMaxFromCorner(selectedOption)
            : { min: 0, max: 0 },
          selectedOptionDiagonalType,
          isCornerRadius,
          options?.removalCorner
        )
      )
      .map(
        drawingTool.createCircleOnCorner(
          parentDrawingShape,
          options?.removalCorner ?? selectedOption
        )
      )
      .forEach((circle) => canvas.add(circle));
  },

  addCirclesToEdgesOfShape: (
    canvas: fabric.Canvas,
    parentDrawingShape: DrawingShape,
    selectedOption: CustomSideModel,
    action: CircleAction
  ): void => {
    const isEnabled = (edgeInfo: DrawingEdge) => {
      if (!edgeInfo.isVisibleInCanvas || action === CircleAction.NONE) {
        return false;
      }

      const conditions: Record<ClickableCircleActions, boolean> = {
        [CircleAction.REMOVE]: edgeInfo.customSideType === selectedOption.type,
        [CircleAction.ADD]: edgeInfo.customSideType !== selectedOption.type,
      };

      return conditions[action];
    };

    const hasValidDistance = ({
      startDisplayVec,
      endDisplayVec,
    }: Pick<DrawingEdge, 'startDisplayVec' | 'endDisplayVec'>) => {
      const distance = getDistanceBetween(startDisplayVec, endDisplayVec);

      return Math.ceil(distance) >= MINIMUM_CUSTOM_SIDE_LENGTH;
    };

    Object.values(parentDrawingShape.drawingEdges)
      .filter((edgeInfo) => isEnabled(edgeInfo) && hasValidDistance(edgeInfo))
      .map(drawingTool.createCircleOnEdge(parentDrawingShape, selectedOption))
      .forEach((circle) => canvas.add(circle));
  },

  /**
   * Removes a canvas object by its name
   * @param canvas the canvas object
   * @param name the name of the canvas object
   * @param strictMode whether to strictly match the name or just match lowercase letters
   */
  removeObjectByName: (
    canvas: fabric.Canvas,
    name: string,
    strictMode = false
  ): void => {
    const nameMatcher = (obj: fabric.Object) =>
      strictMode
        ? obj.name === name
        : obj.name?.toLocaleLowerCase().includes(name.toLocaleLowerCase());

    const removeObjectsFromCanvas = (objects: fabric.Object[]) => {
      objects.forEach((obj) => canvas.remove(obj));
    };

    const removeObjectsFromGroups = (group: fabric.Group) => {
      group
        .getObjects()
        .filter(nameMatcher)
        .forEach((obj) => group.remove(obj));
    };

    const canvasObjects = canvas.getObjects();

    removeObjectsFromCanvas(canvasObjects.filter(nameMatcher));

    if (name !== CANVAS_OBJECT_TYPE_PREFIX.SELECTION_CIRCLE) {
      canvasObjects
        .filter((obj): obj is fabric.Group => isShapeGroup(obj))
        .forEach(removeObjectsFromGroups);
    }

    canvas.renderAll();
  },

  /**
   * update a cornerInfo record with new radius corner product data
   * will remove diagonal length, and set the labels
   * WARNING: MUTATES corner Object
   * @param corner
   * @param product
   * @param size use undefined to select the default value from the product, otherwise this size is used
   */
  setCornerRadiusSize: (
    corner: CornerInfo,
    size: number,
    cornerRadii: CornerRadiusModel[]
  ): void => {
    const currentProduct = getCornerRadiusFromSize(size, cornerRadii);

    // if there's no product matching that value, return to default
    // this happens if the constraints are met, but there are no radius corner products available
    corner.size = (currentProduct && size) ?? DEFAULT_CORNER_RADIUS;

    // this isn't technically necessary as the productId check happens in pricedProducts as well
    corner.productId = currentProduct?.productId;
    corner.label = currentProduct?.name ?? '';

    // makes it a regular corner again
    delete corner.diagonalLength;

    // if it had this before, it needs to be false now
    corner.hasPredefinedExtraEdging = false;

    const radiusLabel = formatCornerRadiusLabel(corner.size);

    if (!corner.labelMeasure?.text) {
      corner.labelMeasure = EditableLabel.newMeasureText(
        radiusLabel,
        corner.labelMeasure?.fill
      );
    }
    if (!corner.labelText?.text) {
      corner.labelText = EditableLabel.newLabelText(radiusLabel);
    }

    corner.labelMeasure?.set('text', radiusLabel);
    corner.labelText?.set('text', radiusLabel);
  },
};

/**
 * Function that triggers edge and corner calculations.
 * @remarks We need to duplicate the drawingTool.edgeAndCornerCalcs line; otherwise, the drawing becomes skewed
 */
const triggerEdgeAndCornersMutations = (
  shape: DrawingShape,
  cornerRadii: CornerRadiusModel[]
) => {
  // Calculating and mutating edges and corners
  drawingTool.edgeAndCornerCalcs(shape);

  // Mutates to enforce constraints
  const constrainedCorners = drawingTool.constraintRadiiSize(
    shape,
    cornerRadii
  );

  if (constrainedCorners.length > 0) {
    // Duplicated but necessary call to ensure the drawing is not skewed and radius constraints are applied
    drawingTool.edgeAndCornerCalcs(shape);
  }
};
